import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { BookingStepUnavailablePage } from '@components/BookingFlow/Common/BookingStepUnavailablePage';
import {
  BookingSummaryLoadingBottomBar,
  BookingSummaryScreenBody
} from '@components/BookingFlow/Common/BookingSummaryScreenBody';
import { getBookingSummaryTitle } from '@components/BookingFlow/Common/getBookingSummaryTitle';
import { MobileBookingSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { NavioContactChatWidget } from '@components/BookingFlow/Common/NavioContactChatWidget';
import { OrganizationBookingAssignee } from '@components/BookingFlow/Common/OrganizationBookingAssignee';
import { OrganizationCarInvoiceReference } from '@components/BookingFlow/Common/OrganizationCarInvoiceReference';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { StationaryInvoicePaymentBanners } from '@components/BookingFlow/Stationary/StationaryInvoicePaymentBanners';
import { StationaryNonMemberOwnerNotice } from '@components/BookingFlow/Stationary/StationaryNonMemberOwnerNotice';
import { StationaryPaymentChoice } from '@components/BookingFlow/Stationary/StationaryPaymentChoice';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { CommentBox } from '@components/Summary/CommentBox';
import { CommonBookingInfoCard } from '@components/Summary/CommonBookingInfoCard';
import { ShoppingCartSummary } from '@components/Summary/ShoppingCartSummary';
import { SilencedCarManagersWarning } from '@components/Summary/SilencedCarManagersWarning';
import { PaymentOptionsInstructions } from '@components/TireOffer/PaymentOptionsInstructions';
import { CustomerDataQualityProvider } from '@components/UserGroupDataQuality/CustomerDataQualityProvider';
import {
  BookingLocationType,
  DeliveryWindowOutputRecord,
  PaymentOption,
  SalesItemBookingCategoryType
} from '@navio/nsdk';
import { BookingAddress, SelectedSalesItemsCar } from 'noddi-types';
import { FixedBottomBarContainer, NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  useAuthContext,
  useBookingActions,
  useBookingAddress,
  useBookingComment,
  useBookingSelectedSalesItemsCars,
  useBookingTimeWindow
} from 'noddi-web';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMobileBookingCheckout } from '../../../../hooks/useMobileBookingCheckout';
import { BrandBanner } from './BrandBanner';
import { ConfirmBooking } from './ConfirmBooking';
import { TermsAndConditions } from './TermsAndConditions';

export const Summary = () => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const bookingTimeWindow = useBookingTimeWindow();
  const selectedAddress = useBookingAddress();
  const { isLoggedIn } = useAuthContext();

  // A missing time window is only a broken draft when the cart needs a customer visit; SummaryContent knows that.
  if (!selectedAddress || !selectedSalesItemsCars.length) {
    return <BookingStepUnavailablePage missing={!selectedAddress ? 'address' : 'services'} />;
  }
  // Login is its own step, so this page is review-and-pay only.
  if (!isLoggedIn) {
    return <Navigate to={customerRoutes.newBookingLogin.getPath()} replace />;
  }
  return (
    <SummaryContent
      selectedSalesItemsCars={selectedSalesItemsCars}
      bookingTimeWindow={bookingTimeWindow}
      selectedAddress={selectedAddress}
    />
  );
};

const SummaryContent = ({
  selectedSalesItemsCars,
  bookingTimeWindow,
  selectedAddress
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  bookingTimeWindow: DeliveryWindowOutputRecord | undefined;
  selectedAddress: BookingAddress;
}) => {
  const [hasAcceptedTermsAndConditions, setHasAcceptedTermsAndConditions] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [invoiceReference, setInvoiceReference] = useState('');
  const { setComment } = useBookingActions();
  const comment = useBookingComment();
  const checkout = useMobileBookingCheckout({ selectedSalesItemsCars, bookingTimeWindow, selectedAddress });

  // Keep the step chrome (title / bottom bar) mounted while data settles.
  if (!checkout.isReady) {
    return (
      <BookingScreen
        progress={getMobileBookingProgress('summary')}
        title={getBookingSummaryTitle()}
        customBottomBar={<BookingSummaryLoadingBottomBar />}
      >
        <BookingSummaryScreenBody
          error={checkout.summaryError}
          onRetry={checkout.retrySummary}
          isRetrying={checkout.isRetrying}
        />
      </BookingScreen>
    );
  }

  const { needsCustomerVisit } = checkout.shoppingCart;
  if (needsCustomerVisit && !bookingTimeWindow) {
    return <BookingStepUnavailablePage missing='timeSlot' />;
  }

  const handleTermsAcceptance = (checked: boolean) => {
    setHasAcceptedTermsAndConditions(checked);
    if (checked && termsError) {
      setTermsError(null);
    }
  };

  const hasStoneChipRepair = selectedSalesItemsCars.some(
    (car) => car.bookingCategoryType === SalesItemBookingCategoryType.STONE_CHIP_REPAIR
  );
  const serviceDepartmentForBooking = checkout.serviceDepartmentForBooking;
  const termsUrl = serviceDepartmentForBooking?.serviceOrganization.termsAndConditionsUrl;

  return (
    <CustomerDataQualityProvider userGroupId={checkout.userGroupId} bookingLocationType={BookingLocationType.MOBILE}>
      <BookingScreen
        progress={getMobileBookingProgress('summary')}
        title={getBookingSummaryTitle()}
        customBottomBar={
          <FixedBottomBarContainer>
            <div className='flex items-center gap-2'>
              <FlowBackButton />
              <NavioContactChatWidget />
            </div>
            <ConfirmBooking
              hasAcceptedTermsAndConditions={hasAcceptedTermsAndConditions}
              hasTermsAndConditions={!!termsUrl}
              onTermsValidationError={setTermsError}
              paymentOption={checkout.paymentOption}
              shouldPayForBooking={checkout.shoppingCart.totalPrice.amount > 0}
              userGroupPayerId={checkout.invoiceToUserGroupId}
              bookingUserGroupId={checkout.bookingParty.userGroupId}
              invoiceReference={
                checkout.invoiceReferenceRule
                  ? { value: invoiceReference, required: checkout.invoiceReferenceRule.required }
                  : undefined
              }
              smsOptIns={checkout.smsOptIns}
              bookingUserId={checkout.bookingAssignee.selectedUserId}
              needsCustomerVisit={needsCustomerVisit}
            />
          </FixedBottomBarContainer>
        }
      >
        <div className='flex flex-col gap-4'>
          <MobileBookingSpikedTireSeasonBanner />
          <div className='flex flex-col gap-2'>
            <CommentBox comment={comment} handleChange={setComment} />
            {termsUrl && serviceDepartmentForBooking && (
              <TermsAndConditions
                hasAcceptedTermsAndConditions={hasAcceptedTermsAndConditions}
                handleTermsAcceptance={handleTermsAcceptance}
                termsError={termsError}
                serviceDepartmentForBooking={serviceDepartmentForBooking}
              />
            )}
          </div>

          <CommonBookingInfoCard
            isBookingProposal={false}
            address={selectedAddress}
            timeWindow={needsCustomerVisit ? bookingTimeWindow : undefined}
            hasCustomerVisit={needsCustomerVisit}
            hasStoneChipRepair={hasStoneChipRepair}
          />

          {checkout.organizationUserGroupId != null && (
            <SilencedCarManagersWarning
              userGroupId={checkout.organizationUserGroupId}
              silencedManagers={checkout.silencedCarManagers}
              smsOptIns={checkout.smsOptIns}
            />
          )}

          <NoddiBasicCard>
            <ShoppingCartSummary shoppingCart={checkout.shoppingCart} selectedSalesItemsCars={selectedSalesItemsCars} />
            <BrandBanner selectedSalesItemsCars={selectedSalesItemsCars} selectedAddressId={selectedAddress.id} />
          </NoddiBasicCard>

          {checkout.nonMemberOwnerNotice && <StationaryNonMemberOwnerNotice notice={checkout.nonMemberOwnerNotice} />}

          <OrganizationBookingAssignee
            assignee={checkout.bookingAssignee.assignee}
            selectedUserId={checkout.bookingAssignee.selectedUserId}
            onSelect={checkout.bookingAssignee.selectUserId}
          />

          {checkout.showPaymentChoice && (
            <StationaryPaymentChoice
              paymentOption={checkout.paymentOption}
              onSelect={checkout.setPaymentOption}
              invoiceRecipientName={checkout.invoiceRecipientName}
            />
          )}

          {checkout.isInvoice && !checkout.showPaymentChoice && !!checkout.invoiceRecipientName && (
            <StationaryInvoicePaymentBanners
              organizationName={checkout.invoiceRecipientName}
              invoiceToName={checkout.invoiceTo?.name}
              showInvoiceBanner
              requiresOrganizationNumber={false}
            />
          )}

          <OrganizationCarInvoiceReference
            rule={checkout.invoiceReferenceRule}
            value={invoiceReference}
            onChange={setInvoiceReference}
          />

          {checkout.paymentOption === PaymentOption.PAY_BEFORE && checkout.shoppingCart.totalPrice.amount > 0 && (
            <PaymentOptionsInstructions />
          )}
        </div>
      </BookingScreen>
    </CustomerDataQualityProvider>
  );
};
